import CloseIcon from "@mui/icons-material/Close";
import {
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
} from "@mui/material";
import { Button } from "pepsico-ds";
import { useRef, useState } from "react";
import { GET_SEGMENT_FILE_UPLOAD } from "../../../graphql/queries/getSegmentFileUpload";
import { useSegmentUsersUpload } from "../../../hooks/useSegmentUsersUpload";
import FileImportStatus from "../../fileImportStatus/FileImportStatus";
import JsonFormsWrapper from "../../jsonForms/jsonFormsWrapper/JsonFormsWrapper";
import { downloadSegmentUsersTemplate } from "../utils/downloadSegmentUsersTemplate";
import {
  clearLastSegmentUploadId,
  getLastSegmentUploadId,
  saveLastSegmentUploadId,
  validateSegmentCsvHeader,
} from "../utils/segmentImportHelpers";
import "./_segment-dialog-modal.scss";

const jsonSchema = {
  type: "object",
  properties: {
    file: {
      type: "array",
      items: { type: "object" },
      title: "Upload a CSV file with users for this segment",
      minItems: 1,
    },
  },
  required: ["file"],
};

const uiSchema = {
  type: "VerticalLayout",
  elements: [
    {
      component: "simpleFileUpload",
      scope: "#/properties/file",
      type: "Control",
      accept: ".csv",
      dragDropText: "Drag and drop your .csv file here",
      multiple: false,
    },
  ],
};

const SegmentUsersImportModal = ({
  open,
  handleClose,
  segmentId,
  refetchSegmentUsers,
  showToast,
}) => {
  const [file, setFile] = useState({});
  const [uploading, setUploading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [doNotShowCheck, setDoNotShowCheck] = useState(false);
  const [uploadId, setUploadId] = useState(() => getLastSegmentUploadId(segmentId));
  const [headerError, setHeaderError] = useState(null);
  const startedHereRef = useRef(false);
  const { handleUpload } = useSegmentUsersUpload();

  const onModalClose = () => {
    setFile({});
    setHeaderError(null);
    setUploadId(null);
    handleClose?.();
    confirmModalClose();
  };

  const confirmModalClose = () => {
    setConfirmOpen(false);
    setDoNotShowCheck(false);
  };

  const onDoNotShowCheckChange = (e) => {
    setDoNotShowCheck(e.target.checked);
  };

  const showConfirmationModal = () => {
    if (!file?.file) return;
    const flag = localStorage.getItem("doNotShowImportSegmentUsersModal");
    if (flag === "true") {
      return handleImport();
    }
    setConfirmOpen(true);
  };

  const handleImport = async (setLocalValue = false) => {
    if (!file?.file) return;
    setConfirmOpen(false);
    if (setLocalValue) {
      localStorage.setItem("doNotShowImportSegmentUsersModal", doNotShowCheck);
    }
    setHeaderError(null);
    let headerProblem;
    try {
      headerProblem = await validateSegmentCsvHeader(file.file);
    } catch (error) {
      console.error("Could not read the segment users file:", error);
      headerProblem = "Could not read the file. Please choose it again.";
    }
    if (headerProblem) {
      setHeaderError(headerProblem);
      return;
    }
    setUploading(true);
    const response = await handleUpload(file.file, segmentId);
    setUploading(false);

    if (!response.status) {
      showToast?.("error", `Segment users import failed. ${response.message}`);
      return;
    }

    startedHereRef.current = true;
    saveLastSegmentUploadId(segmentId, response.uploadId);
    setUploadId(response.uploadId);
    setFile({});
  };

  const handleTerminal = (upload, { sawActive } = {}) => {
    // Only an import that is still running is restored on the next visit; a
    // finished one must not come back as a stale status.
    clearLastSegmentUploadId(segmentId);
    if (upload.uploadStatus === "COMPLETED") refetchSegmentUsers?.();
    if (!startedHereRef.current && !sawActive) {
      // Restored from an earlier visit and already finished: show a clean dialog
      setUploadId(null);
      return;
    }
    if (!startedHereRef.current) return;
    startedHereRef.current = false;
    if (upload.uploadStatus === "COMPLETED") {
      showToast?.(
        upload.errorCount > 0 ? "warning" : "success",
        upload.errorCount > 0
          ? `Imported ${upload.importedRows} rows. Some rows were skipped.`
          : "Segment users imported successfully"
      );
    } else {
      showToast?.("error", "Segment users import failed. No users were changed.");
    }
  };

  const getImportButtonText = () => (uploading ? "Uploading..." : "Import users");

  return (
    <>
      <Dialog
        open={open}
        onClose={onModalClose}
        className="segment-dialog-modal view"
        fullWidth
        maxWidth={false}
      >
        <DialogTitle>
          <Grid container justifyContent="space-between" direction="row">
            <Grid item xs={10}>
              <h2>Import segment users</h2>
            </Grid>
            <Grid item xs={1} display="flex" justifyContent="flex-end">
              <CloseIcon onClick={onModalClose} className="cursor-pointer" />
            </Grid>
          </Grid>
        </DialogTitle>
        <DialogContent>
          <div className="dialog-content">
            <ul className="segment-import-rules font-xs">
              <li>
                Columns: <code>action</code> (Add or Remove), at least one of{" "}
                <code>profile_id</code>, <code>user_id</code>,{" "}
                <code>national_id</code>, and optionally <code>segment_id</code>{" "}
                (must match this segment).
              </li>
              <li>Maximum 500 MB and 500,000 rows.</li>
              <li>Blank rows fail the file; remove them before uploading.</li>
            </ul>
            <JsonFormsWrapper
              jsonschema={jsonSchema}
              uischema={uiSchema}
              data={file}
              onChange={(data) => {
                setFile(data);
                setHeaderError(null);
                // Hide the previous import's status once a new file is chosen
                if (data?.file?.name) setUploadId(null);
              }}
            />
            {file?.file?.name && (
              <div className="mt-2">
                <span className="font-xs">{file?.file?.name}</span>
              </div>
            )}
            {headerError && (
              <p
                className="mt-2 font-xs text-danger"
                style={{ color: "#d32f2f" }}
                role="alert"
                data-testid="header-error"
              >
                {headerError}
              </p>
            )}
            <FileImportStatus
              query={GET_SEGMENT_FILE_UPLOAD}
              dataKey="getSegmentFileUpload"
              variables={uploadId ? { segmentId, uploadId } : null}
              onTerminal={handleTerminal}
              failedMessage="The file was not imported and no users were changed."
            />
          </div>
        </DialogContent>
        <DialogActions>
          <Button variant="tertiary" text="Cancel" onClick={onModalClose} />
          <Button
            variant="secondary"
            text="Download template"
            onClick={() => downloadSegmentUsersTemplate(segmentId)}
          />
          <Button
            variant="primary"
            text={getImportButtonText()}
            disabled={!file?.file?.name || uploading}
            onClick={showConfirmationModal}
          />
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmOpen}
        onClose={confirmModalClose}
        className="segment-dialog-modal view confirm-file-modal"
        fullWidth={false}
        maxWidth={"xs"}
      >
        <DialogContent>
          <div className="dialog-content">
            <div className="font-lg font-bold">
              Are you sure you want to import users for this segment with the
              uploaded file?
            </div>
            <div className="mb-2">
              <h6>*Once confirmed, import will be processed automatically.</h6>
            </div>
            <div>
              <FormControlLabel
                control={
                  <Checkbox
                    id={`checkbox-confirm-file`}
                    onChange={onDoNotShowCheckChange}
                  />
                }
                label="Do not show this message again"
              />
            </div>
          </div>
        </DialogContent>
        <DialogActions>
          <Button variant="tertiary" text="Cancel" onClick={confirmModalClose} />
          <Button
            variant="primary"
            text={"Confirm"}
            disabled={!file?.file?.name || uploading}
            onClick={() => handleImport(true)}
            isLoading={uploading}
          />
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SegmentUsersImportModal;
